/**
 * A sample counterfactual study.
 *
 * Built so the screen must handle the case a demo would avoid: one variant that
 * moves work, one that moves it the wrong way, and TWO THAT DO NOTHING. On a
 * half-loaded estate the inert ones are the finding — "money is not your
 * constraint" is the most useful sentence this feature produces — and a mock
 * where every lever worked would let a screen ship that treats an empty diff as
 * a rendering edge case.
 */
import type { Study } from "../domain/counterfactual";

export function mockStudy(fiscalYear: number, crews = 1): Study {
  return {
    available: true,
    fiscalYear,
    currency: "SAR",
    baseline: {
      envelope: 6_000_000,
      crews,
      totals: {
        scheduled: 4,
        scheduledCost: 5_000_000,
        deferred: 2,
        deferredEscalation: 418_000,
        undecidable: 4,
        envelopeUnspent: 1_000_000,
        blockedByMissingFacts: 690_000,
      },
      note: "4 of 10 candidates are scheduled, 2 deferred, 4 could not be decided at all.",
    },
    variants: [
      {
        kind: "envelope",
        change: "+1,500,000 on the envelope",
        envelope: 7_500_000,
        diff: {
          moved: [
            { id: "P-5", title: "Rubber removal, RWY 17 touchdown", from: "deferred", to: "scheduled" },
          ],
          scheduledDelta: 1,
          costDelta: 1_400_000,
          escalationDelta: -336_000,
          undecidableDelta: 0,
        },
        reading:
          "+1,500,000 on the envelope: 1 job(s) become schedulable. It avoids 336,000 of escalation on the deferred work.",
      },
      {
        kind: "crew",
        change: "2 crews instead of 1",
        crews: 2,
        diff: {
          moved: [
            { id: "P-6", title: "Reseal apron south expansion joints", from: "deferred", to: "scheduled" },
          ],
          scheduledDelta: 1,
          costDelta: 720_000,
          escalationDelta: -82_000,
          undecidableDelta: 0,
        },
        reading:
          "2 crews instead of 1: 1 job(s) become schedulable. It avoids 82,000 of escalation on the deferred work.",
      },
      {
        kind: "envelope",
        change: "−1,500,000 on the envelope",
        envelope: 4_500_000,
        diff: {
          moved: [
            { id: "P-4", title: "Replace AGL series, TWY C", from: "scheduled", to: "deferred" },
          ],
          scheduledDelta: -1,
          costDelta: -880_000,
          escalationDelta: 0,
          undecidableDelta: 0,
        },
        reading: "−1,500,000 on the envelope: 1 drop out of the schedule.",
      },
      {
        /* the finding: the lever an operator reaches for first, and it is inert */
        kind: "envelope",
        change: "+3,000,000 on the envelope",
        envelope: 9_000_000,
        diff: {
          moved: [],
          scheduledDelta: 0,
          costDelta: 0,
          escalationDelta: 0,
          undecidableDelta: 0,
        },
        reading:
          "+3,000,000 on the envelope changes nothing in this programme. The binding constraint is not this: " +
          "4 candidate(s) cannot be decided at all, holding up 690,000 of already-funded work.",
      },
    ],
    information: {
      desks: [
        {
          desk: "whoever raised the candidate",
          facts: 2,
          worth: 430_000,
          candidates: [
            {
              title: "Seal TWY A, chainage 0–900",
              missing: "the candidate names neither a stand nor a zone, so there is no position to test a closure against",
            },
            {
              title: "Pavement section predicted to fall below intervention PCI — PAV-014",
              missing: "no treatment category on the candidate",
            },
          ],
        },
        {
          desk: "whoever owns the term contract or rate schedule",
          facts: 2,
          worth: 260_000,
          candidates: [
            { title: "Recoat mandatory sign SGN-114", missing: "no unit rate for 'sign_recoat' at this site" },
            {
              title: "Repaint stand markings, apron east",
              missing: "no weather limits are on file for 'line_marking', so no window can be tested",
            },
          ],
        },
      ],
      factsOutstanding: 4,
      worth: 690_000,
      reading:
        "4 missing fact(s) across 2 desk(s) are holding 690,000 of already-priced work out of the programme.",
      limit:
        "Supplying a fact makes a candidate DECIDABLE — it does not schedule it. Whether a slot exists depends " +
        "on traffic and weather at a position that, by definition, nobody has recorded yet. A decidable job may " +
        "still be deferred, and this figure is what becomes possible to answer, not what becomes possible to do.",
    },
    oneChangeOnly:
      "Each variant alters exactly ONE input and is labelled with it. A scenario that moved two would produce " +
      "an outcome attributable to neither, which is how a business case stops being one.",
    notAForecast:
      "These are not predictions. Each says what this platform would have decided on today's register given a " +
      "different input — nothing here claims to know what will happen.",
  };
}
